// Connects the playback-rate preference in src/scripts/playbackRate.js to the
// three things it deliberately does not import: the device cache at startup,
// the audioPlaybackSpeed flag, and the RTDB writer its mirror uses.
//
// Kept apart from playbackRate.js so that the playback service and the pill
// components can read the effective rate without pulling Firebase, the auth
// store or the flag store into their import graph (their unit tests load them
// with only Capacitor mocked). Called once from src/main.js, after
// initFeatureFlags.

import { watch } from "vue";
import { getDatabase, ref, set } from "firebase/database";
import { authStore } from "@/store/auth";
import { featureFlags } from "@/store/featureFlags.js";
import {
  flushPlaybackRateMirror,
  forgetPlaybackRate,
  loadCachedPlaybackRate,
  playbackRateState,
  registerPlaybackRateMirror,
} from "@/scripts/playbackRate.js";

let initialized = false;

export function initPlaybackRateSync() {
  if (initialized) return;
  initialized = true;
  loadCachedPlaybackRate();
  registerPlaybackRateMirror({
    currentUid: () => authStore.getUserId(),
    write: (uid, value) =>
      set(ref(getDatabase(), `users/${uid}/audioPlaybackRate`), value),
  });
  // The preference belongs to the signed-in account. Sign-out clears it
  // (authStore.clearPlaybackRate), but a session can also end without it: a
  // revoked or expired token only sets userId to null, and another account can
  // replace the signed-in one. Either way the next learner on this device must
  // not inherit the speed. A sign-in from signed out keeps the cache, which is
  // what restores the learner's own speed at startup.
  watch(
    () => authStore.userId,
    (next, previous) => {
      if (previous && next !== previous) forgetPlaybackRate();
    }
  );
  // The mirror waits for the chip to rest, and that timer does not survive
  // the app being closed. Hiding the page (backgrounding the app, switching
  // tab, closing it) sends a waiting choice at once.
  if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushPlaybackRateMirror();
    });
  }
  // A watcher rather than a one-time read: flags start false and flip moments
  // after load (src/store/featureFlags.js), and an admin can turn the flag off
  // mid-session, which must drop every rate back to 1x at once.
  watch(
    () => featureFlags.audioPlaybackSpeed === true,
    (enabled) => {
      playbackRateState.speedEnabled = enabled;
    },
    { immediate: true }
  );
}
